import React from "react";
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend
} from "recharts";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CodeIcon from '@mui/icons-material/Code';
import EventIcon from '@mui/icons-material/Event';
import './PMDashboard.css';
import { Divider, Typography, useTheme } from "@mui/material";
import ProjectExplorer from "./ProjectExplorer";
import ClientDashboard from "./clientDashboard";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Admin Charts Data
const projectProgressData = [
  { month: "Jan", completed: 5 },
  { month: "Feb", completed: 8 },
  { month: "Mar", completed: 10 },
  { month: "Apr", completed: 7 },
  { month: "May", completed: 12 },
  { month: "Jun", completed: 15 },
];

const taskStatusData = [
  { name: "To Do", count: 30 },
  { name: "In Progress", count: 45 },
  { name: "Completed", count: 80 },
  { name: "Blocked", count: 5 },
];

const teamProjectData = [
  { name: "Frontend", count: 5 },
  { name: "Backend", count: 7 },
  { name: "DevOps", count: 3 },
  { name: "QA", count: 4 },
];

// User Data
const userTaskData = [
  { month: "Jan", completed: 12 },
  { month: "Feb", completed: 15 },
  { month: "Mar", completed: 18 },
  { month: "Apr", completed: 20 },
];

const userTaskBreakdown = [
  { name: "To Do", value: 3 },
  { name: "In Progress", value: 5 },
  { name: "Completed", value: 12 },
];

// Cards
const adminStats = [
  { title: "Total Projects", value: "32", icon: <AssignmentIcon />, color: "#1976d2", subtitle: "Across all teams" },
  { title: "Tasks In Progress", value: "45", icon: <WorkOutlineIcon />, color: "#ffc107", subtitle: "Active tasks" },
  { title: "Completed This Month", value: "67", icon: <TrendingUpIcon />, color: "#4caf50", subtitle: "Productivity" },
  { title: "Active Members", value: "18", icon: <GroupIcon />, color: "#00acc1", subtitle: "Contributing users" },
];

const userStats = [
  { title: "My Tasks", value: "20", icon: <AssignmentIcon />, color: "#1976d2", subtitle: "Assigned to me" },
  { title: "Completed", value: "12", icon: <EmojiEventsIcon />, color: "#4caf50", subtitle: "This month" },
  { title: "Time Logged", value: "42h", icon: <EventIcon />, color: "#ab47bc", subtitle: "Total hours" },
  { title: "Top Skill", value: "React.js", icon: <CodeIcon />, color: "#e53935", subtitle: "Based on tasks" },
];

function StatCard({ theme, title, value, icon, color, subtitle, progress }) {
  return (
    <div className={`stat-card ${theme}`}>
      <div className="stat-icon" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="stat-content">
        <h4>{title}</h4>
        <h2>{value}</h2>
        {subtitle && <p>{subtitle}</p>}
        {progress !== undefined && (
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        )}
      </div>
    </div>
  );
}

function PMDashboard() {
  const theme = useTheme();
  const { access } = useAuthStore();

  const { logout, userData, setAccess, login } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const role = userData?.organizationuser?.user_types?.user_type_name?.toLowerCase();
  const isClient = role === "client";

  const isAdmin = access === "Admin";
  const showFullDashboard = Permission && Permission.includes("VIEW_ALL_TASKS");
  const statsToShow = isAdmin ? adminStats : userStats;

  return (
    <div className="dashboard-container">
      {userData && <>
        {isClient ? <ClientDashboard /> : <ProjectExplorer />}

        {!isClient && showFullDashboard && (
          <>
            <Divider />
            <Typography mt={2} variant="h6" fontWeight="bold" gutterBottom>
              Project Dashboard
            </Typography>
  
            <div className="stats-grid">
              {statsToShow.map((stat, index) => (
                <StatCard key={index} {...stat} theme={theme.palette.mode} />
              ))}
            </div>
  
            <h2>Charts Overview</h2>
            <div className="charts-grid">
  
              <div className={`chart-card ${theme.palette.mode}`}>
                <h3>Your Task Completion Trend</h3>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={userTaskData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="completed" stroke="#1976d2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
  
              <div className={`chart-card ${theme.palette.mode}`}>
                <h3>Your Task Distribution</h3>
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={userTaskBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {userTaskBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
  
            </div>
  
          </>
        )}
      </>}

    </div>
  );
}

export default PMDashboard;
